import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * AppShell - Application Layout
 *
 * Modern SaaS dashboard layout with light/dark theme support.
 * Matches the reference design with clean, professional styling.
 *
 * Header contains:
 * - Logo and branding
 * - Global search bar
 * - Action buttons (Scan, Radio Check, Refresh)
 * - System status badge
 * - Notification bell icon
 * - Settings icon
 * - Live clock display
 * - Theme toggle
 */
import { useState, useEffect } from 'react';
import { AppShell as MantineAppShell, Box, Burger, Collapse, Group, Text, UnstyledButton, Tooltip, Menu, } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
/** Theme toggle hook */
function useTheme() {
    const [isDark, setIsDark] = useState(false);
    useEffect(() => {
        // Check localStorage or system preference on mount
        const stored = localStorage.getItem('theme');
        if (stored === 'dark') {
            setIsDark(true);
            document.documentElement.classList.add('dark');
        }
        else if (stored === 'light') {
            setIsDark(false);
            document.documentElement.classList.remove('dark');
        }
    }, []);
    const toggleTheme = () => {
        const newIsDark = !isDark;
        setIsDark(newIsDark);
        if (newIsDark) {
            document.documentElement.classList.add('dark');
            localStorage.setItem('theme', 'dark');
        }
        else {
            document.documentElement.classList.remove('dark');
            localStorage.setItem('theme', 'light');
        }
    };
    return { isDark, toggleTheme };
}
/** Clock hook for live time display */
function useClock() {
    const [time, setTime] = useState(() => {
        const now = new Date();
        return now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    });
    useEffect(() => {
        const interval = setInterval(() => {
            const now = new Date();
            setTime(now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
        }, 1000);
        return () => clearInterval(interval);
    }, []);
    return time;
}
import { Link, useLocation } from '@tanstack/react-router';
import { IconDashboard, IconTopology, IconClock, IconDevicesPc, IconUsers, IconBuildingCommunity, IconMapPin, IconRadioTower, IconChart, IconClipboardList, } from '@/components/icons';
/** Chevron icon for collapsible sections */
function IconChevron({ expanded, size = 16 }) {
    return (_jsx("svg", { width: size, height: size, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", style: {
            transition: 'transform 150ms ease',
            transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
        }, children: _jsx("polyline", { points: "9 18 15 12 9 6" }) }));
}
/** Settings/Cog icon */
function IconSettings({ size = 20 }) {
    return (_jsxs("svg", { width: size, height: size, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [_jsx("circle", { cx: "12", cy: "12", r: "3" }), _jsx("path", { d: "M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" })] }));
}
/** Radio/broadcast icon for logo */
function IconRadio({ size = 18 }) {
    return (_jsxs("svg", { width: size, height: size, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [_jsx("circle", { cx: "12", cy: "12", r: "2" }), _jsx("path", { d: "M16.24 7.76a6 6 0 0 1 0 8.49m-8.48-.01a6 6 0 0 1 0-8.49m11.31-2.82a10 10 0 0 1 0 14.14m-14.14 0a10 10 0 0 1 0-14.14" })] }));
}
/** Sun icon for light mode */
function IconSun({ size = 20 }) {
    return (_jsxs("svg", { width: size, height: size, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [_jsx("circle", { cx: "12", cy: "12", r: "5" }), _jsx("line", { x1: "12", y1: "1", x2: "12", y2: "3" }), _jsx("line", { x1: "12", y1: "21", x2: "12", y2: "23" }), _jsx("line", { x1: "4.22", y1: "4.22", x2: "5.64", y2: "5.64" }), _jsx("line", { x1: "18.36", y1: "18.36", x2: "19.78", y2: "19.78" }), _jsx("line", { x1: "1", y1: "12", x2: "3", y2: "12" }), _jsx("line", { x1: "21", y1: "12", x2: "23", y2: "12" }), _jsx("line", { x1: "4.22", y1: "19.78", x2: "5.64", y2: "18.36" }), _jsx("line", { x1: "18.36", y1: "5.64", x2: "19.78", y2: "4.22" })] }));
}
/** Moon icon for dark mode */
function IconMoon({ size = 20 }) {
    return (_jsx("svg", { width: size, height: size, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: _jsx("path", { d: "M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" }) }));
}
/** Search icon */
function IconSearch({ size = 16 }) {
    return (_jsxs("svg", { width: size, height: size, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [_jsx("circle", { cx: "11", cy: "11", r: "8" }), _jsx("line", { x1: "21", y1: "21", x2: "16.65", y2: "16.65" })] }));
}
/** Bell/Notification icon */
function IconBell({ size = 18 }) {
    return (_jsxs("svg", { width: size, height: size, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [_jsx("path", { d: "M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" }), _jsx("path", { d: "M13.73 21a2 2 0 0 1-3.46 0" })] }));
}
/** Clock icon (header) */
function IconClockDisplay({ size = 14 }) {
    return (_jsxs("svg", { width: size, height: size, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [_jsx("circle", { cx: "12", cy: "12", r: "10" }), _jsx("polyline", { points: "12 6 12 12 16 14" })] }));
}
/** Navigation link component */
function NavItem({ to, icon: Icon, label, active, indent = false, }) {
    return (_jsxs(UnstyledButton, { component: Link, to: to, style: {
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '10px 16px',
            paddingLeft: indent ? 44 : 16,
            borderRadius: 6,
            color: active ? '#3B82F6' : 'var(--text-secondary)',
            backgroundColor: active ? 'var(--blue-bg)' : 'transparent',
            borderLeft: active ? '3px solid #3B82F6' : '3px solid transparent',
            transition: 'all 150ms ease',
            textDecoration: 'none',
            fontSize: '0.875rem',
            fontWeight: active ? 600 : 500,
        }, onMouseEnter: (e) => {
            if (!active) {
                e.currentTarget.style.backgroundColor = 'var(--bg-base)';
                e.currentTarget.style.color = 'var(--text-primary)';
            }
        }, onMouseLeave: (e) => {
            if (!active) {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = 'var(--text-secondary)';
            }
        }, children: [_jsx(Icon, { size: 18 }), _jsx("span", { children: label })] }));
}
/** Collapsible section header */
function SectionHeader({ icon: Icon, label, expanded, onToggle, hasActiveChild, }) {
    return (_jsxs(UnstyledButton, { onClick: onToggle, style: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            padding: '10px 16px',
            borderRadius: 6,
            color: hasActiveChild ? 'var(--text-primary)' : 'var(--text-muted)',
            backgroundColor: 'transparent',
            transition: 'all 150ms ease',
            fontSize: '0.75rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
        }, onMouseEnter: (e) => {
            e.currentTarget.style.backgroundColor = 'var(--bg-base)';
            e.currentTarget.style.color = 'var(--text-secondary)';
        }, onMouseLeave: (e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = hasActiveChild ? 'var(--text-primary)' : 'var(--text-muted)';
        }, children: [_jsxs(Group, { gap: 10, children: [_jsx(Icon, { size: 16 }), _jsx("span", { children: label })] }), _jsx(IconChevron, { expanded: expanded, size: 14 })] }));
}
/** Section label */
function SectionLabel({ children }) {
    return (_jsx(Text, { style: {
            fontSize: '0.7rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: 'var(--text-muted)',
            padding: '16px 16px 8px',
        }, children: children }));
}
/** Section divider */
function Divider() {
    return (_jsx(Box, { style: {
            height: 1,
            backgroundColor: 'var(--border-colour)',
            margin: '12px 16px',
        } }));
}
/** Header icon button */
function HeaderIconButton({ icon: Icon, tooltip, onClick, badge, }) {
    const [isHovered, setIsHovered] = useState(false);
    return (_jsx(Tooltip, { label: tooltip, children: _jsxs(UnstyledButton, { onClick: onClick, onMouseEnter: () => setIsHovered(true), onMouseLeave: () => setIsHovered(false), style: {
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 36,
                height: 36,
                borderRadius: 8,
                backgroundColor: isHovered ? 'var(--blue-bg)' : 'var(--bg-base)',
                border: `1px solid ${isHovered ? '#3B82F6' : 'var(--border-colour)'}`,
                color: isHovered ? '#3B82F6' : 'var(--text-secondary)',
                transition: 'all 150ms ease',
            }, children: [_jsx(Icon, { size: 18 }), badge !== undefined && badge > 0 && (_jsx(Box, { style: {
                        position: 'absolute',
                        top: -4,
                        right: -4,
                        width: 18,
                        height: 18,
                        borderRadius: '50%',
                        backgroundColor: '#EF4444',
                        color: '#FFFFFF',
                        fontSize: '0.6875rem',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }, children: badge > 9 ? '9+' : badge }))] }) }));
}
/** System status badge */
function StatusBadge({ status }) {
    const config = {
        online: {
            label: 'Online',
            bgColour: 'var(--green-bg)',
            textColour: '#22C55E',
            dotColour: '#22C55E',
        },
        degraded: {
            label: 'Degraded',
            bgColour: 'var(--amber-bg)',
            textColour: '#F59E0B',
            dotColour: '#F59E0B',
        },
        offline: {
            label: 'Offline',
            bgColour: 'var(--red-bg)',
            textColour: '#EF4444',
            dotColour: '#EF4444',
        },
    };
    const { label, bgColour, textColour, dotColour } = config[status];
    return (_jsxs(Box, { style: {
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '6px 12px',
            borderRadius: 20,
            backgroundColor: bgColour,
            fontSize: '0.75rem',
            fontWeight: 600,
            color: textColour,
        }, children: [_jsx(Box, { style: {
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: dotColour,
                    animation: status === 'online' ? 'livePulse 2s ease-in-out infinite' : 'none',
                } }), _jsx("span", { children: label })] }));
}
export function AppShell({ children }) {
    const [opened, { toggle }] = useDisclosure();
    const location = useLocation();
    const [configExpanded, setConfigExpanded] = useState(false);
    const [analyticsExpanded, setAnalyticsExpanded] = useState(false);
    const { isDark, toggleTheme } = useTheme();
    const [searchValue, setSearchValue] = useState('');
    const clockTime = useClock();
    // System status - would come from API in production
    const systemStatus = 'online';
    const notificationCount = 0;
    const pathname = location.pathname;
    // Check if any config routes are active
    const configRoutes = ['/equipment', '/users', '/departments', '/locations', '/gateway', '/status'];
    const isConfigActive = configRoutes.some((r) => pathname === r || pathname.startsWith(r + '/'));
    // Check if any analytics routes are active
    const analyticsRoutes = ['/reports', '/audit'];
    const isAnalyticsActive = analyticsRoutes.some((r) => pathname === r || pathname.startsWith(r + '/'));
    // Auto-expand sections when child is active
    if (isConfigActive && !configExpanded) {
        setConfigExpanded(true);
    }
    if (isAnalyticsActive && !analyticsExpanded) {
        setAnalyticsExpanded(true);
    }
    return (_jsxs(MantineAppShell, { header: { height: 60 }, navbar: {
            width: 260,
            breakpoint: 'sm',
            collapsed: { mobile: !opened },
        }, padding: 0, children: [_jsx(MantineAppShell.Header, { style: {
                    backgroundColor: 'var(--bg-card)',
                    borderBottom: '1px solid var(--border-colour)',
                    transition: 'background-color 200ms ease, border-color 200ms ease',
                }, children: _jsxs(Group, { h: "100%", px: "md", justify: "space-between", children: [_jsxs(Group, { gap: "sm", children: [_jsx(Burger, { opened: opened, onClick: toggle, hiddenFrom: "sm", size: "sm", color: "var(--text-secondary)" }), _jsxs(Group, { gap: 10, children: [_jsx(Box, { style: {
                                                width: 32,
                                                height: 32,
                                                borderRadius: 8,
                                                background: 'linear-gradient(135deg, #1E293B 0%, #334155 100%)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: '#FFFFFF',
                                            }, children: _jsx(IconRadio, { size: 18 }) }), _jsxs(Box, { children: [_jsx(Text, { style: {
                                                        fontFamily: '"Inter", sans-serif',
                                                        fontSize: '1rem',
                                                        fontWeight: 700,
                                                        color: 'var(--text-primary)',
                                                        letterSpacing: '-0.02em',
                                                        lineHeight: 1.2,
                                                    }, children: "Commander" }), _jsx(Text, { style: {
                                                        fontFamily: '"Inter", sans-serif',
                                                        fontSize: '0.7rem',
                                                        fontWeight: 500,
                                                        color: 'var(--text-muted)',
                                                        letterSpacing: '0.01em',
                                                        lineHeight: 1,
                                                    }, children: "Radio Asset Management" })] }), _jsx(Box, { style: {
                                                height: 24,
                                                width: 1,
                                                backgroundColor: 'var(--border-colour)',
                                                marginLeft: 4,
                                            } }), _jsx(Box, { style: {
                                                display: 'flex',
                                                alignItems: 'center',
                                                marginLeft: 8,
                                            }, children: _jsx("img", { src: "/images/servicom-logo.png", alt: "Servicom", style: {
                                                    height: 18,
                                                    width: 'auto',
                                                    objectFit: 'contain',
                                                } }) })] })] }), _jsxs(Box, { style: {
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                padding: '8px 16px',
                                borderRadius: 8,
                                backgroundColor: 'var(--bg-base)',
                                border: '1px solid var(--border-colour)',
                                transition: 'all 150ms ease',
                            }, children: [_jsx(Box, { style: { color: 'var(--text-muted)' }, children: _jsx(IconSearch, { size: 16 }) }), _jsx("input", { type: "text", placeholder: "Search radios, users, or sessions...", value: searchValue, onChange: (e) => setSearchValue(e.target.value), style: {
                                        border: 'none',
                                        outline: 'none',
                                        background: 'transparent',
                                        color: 'var(--text-primary)',
                                        fontSize: '0.875rem',
                                        width: 260,
                                        fontFamily: 'inherit',
                                    } })] }), _jsxs(Group, { gap: "md", children: [_jsx(StatusBadge, { status: systemStatus }), _jsx(Box, { style: { width: 1, height: 24, backgroundColor: 'var(--border-colour)' } }), _jsx(Group, { gap: 8, children: _jsxs(Menu, { shadow: "md", width: 320, position: "bottom-end", children: [_jsx(Menu.Target, { children: _jsxs(Box, { style: { position: 'relative' }, children: [_jsx(HeaderIconButton, { icon: IconBell, tooltip: "Notifications" }), notificationCount > 0 && (_jsx(Box, { style: {
                                                                position: 'absolute',
                                                                top: -4,
                                                                right: -4,
                                                                width: 18,
                                                                height: 18,
                                                                borderRadius: '50%',
                                                                backgroundColor: '#EF4444',
                                                                color: '#FFFFFF',
                                                                fontSize: '0.6875rem',
                                                                fontWeight: 600,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                pointerEvents: 'none',
                                                            }, children: notificationCount > 9 ? '9+' : notificationCount }))] }) }), _jsxs(Menu.Dropdown, { style: { backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-colour)' }, children: [_jsx(Box, { style: { padding: '12px 16px', borderBottom: '1px solid var(--border-colour)' }, children: _jsx(Text, { style: { fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-primary)' }, children: "Notifications" }) }), _jsx(Box, { style: { padding: '24px 16px', textAlign: 'center' }, children: _jsx(Text, { style: { fontSize: '0.8125rem', color: 'var(--text-muted)' }, children: "No notifications" }) })] })] }) }), _jsxs(Box, { style: {
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        padding: '6px 12px',
                                        borderRadius: 6,
                                        backgroundColor: 'var(--bg-base)',
                                        border: '1px solid var(--border-colour)',
                                    }, children: [_jsx(Box, { style: { color: 'var(--text-muted)' }, children: _jsx(IconClockDisplay, { size: 14 }) }), _jsx(Text, { style: {
                                                fontFamily: "'SF Mono', 'Menlo', 'Monaco', monospace",
                                                fontSize: '0.875rem',
                                                fontWeight: 500,
                                                color: 'var(--text-secondary)',
                                            }, children: clockTime })] }), _jsx(Tooltip, { label: isDark ? 'Switch to light mode' : 'Switch to dark mode', children: _jsx(UnstyledButton, { onClick: toggleTheme, style: {
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: 36,
                                            height: 36,
                                            borderRadius: 8,
                                            backgroundColor: 'var(--bg-base)',
                                            border: '1px solid var(--border-colour)',
                                            color: 'var(--text-secondary)',
                                            transition: 'all 150ms ease',
                                        }, onMouseEnter: (e) => {
                                            e.currentTarget.style.backgroundColor = 'var(--blue-bg)';
                                            e.currentTarget.style.color = '#3B82F6';
                                            e.currentTarget.style.borderColor = '#3B82F6';
                                        }, onMouseLeave: (e) => {
                                            e.currentTarget.style.backgroundColor = 'var(--bg-base)';
                                            e.currentTarget.style.color = 'var(--text-secondary)';
                                            e.currentTarget.style.borderColor = 'var(--border-colour)';
                                        }, children: isDark ? _jsx(IconSun, { size: 18 }) : _jsx(IconMoon, { size: 18 }) }) })] })] }) }), _jsx(MantineAppShell.Navbar, { style: {
                    backgroundColor: 'var(--bg-card)',
                    borderRight: '1px solid var(--border-colour)',
                    transition: 'background-color 200ms ease, border-color 200ms ease',
                }, children: _jsxs(Box, { style: { padding: '8px 8px', flex: 1, display: 'flex', flexDirection: 'column' }, children: [_jsxs(Box, { children: [_jsx(SectionLabel, { children: "Operations" }), _jsx(NavItem, { to: "/", icon: IconDashboard, label: "Command Centre", active: pathname === '/' }), _jsx(NavItem, { to: "/sessions", icon: IconClock, label: "Sessions", active: pathname === '/sessions' }), _jsx(NavItem, { to: "/topology", icon: IconTopology, label: "Network", active: pathname === '/topology' })] }), _jsx(Divider, {}), _jsxs(Box, { children: [_jsx(SectionHeader, { icon: IconSettings, label: "Configuration", expanded: configExpanded, onToggle: () => setConfigExpanded(!configExpanded), hasActiveChild: isConfigActive }), _jsx(Collapse, { in: configExpanded, children: _jsxs(Box, { children: [_jsx(NavItem, { to: "/equipment", icon: IconDevicesPc, label: "Equipment", active: pathname === '/equipment', indent: true }), _jsx(NavItem, { to: "/users", icon: IconUsers, label: "Users", active: pathname === '/users', indent: true }), _jsx(NavItem, { to: "/departments", icon: IconBuildingCommunity, label: "Departments", active: pathname === '/departments', indent: true }), _jsx(NavItem, { to: "/locations", icon: IconMapPin, label: "Locations", active: pathname === '/locations', indent: true }), _jsx(NavItem, { to: "/gateway", icon: IconRadioTower, label: "Gateway", active: pathname === '/gateway', indent: true })] }) })] }), _jsx(Divider, {}), _jsxs(Box, { children: [_jsx(SectionHeader, { icon: IconChart, label: "Analytics", expanded: analyticsExpanded, onToggle: () => setAnalyticsExpanded(!analyticsExpanded), hasActiveChild: isAnalyticsActive }), _jsx(Collapse, { in: analyticsExpanded, children: _jsxs(Box, { children: [_jsx(NavItem, { to: "/reports", icon: IconChart, label: "Reports", active: pathname === '/reports', indent: true }), _jsx(NavItem, { to: "/audit", icon: IconClipboardList, label: "Audit Log", active: pathname === '/audit', indent: true })] }) })] }), _jsx(Box, { style: { flex: 1 } }), _jsx(Box, { style: {
                                padding: '12px 16px',
                                borderTop: '1px solid var(--border-colour)',
                            }, children: _jsxs(Group, { gap: 8, children: [_jsx(Box, { style: {
                                            width: 8,
                                            height: 8,
                                            borderRadius: '50%',
                                            backgroundColor: '#22C55E',
                                            boxShadow: '0 0 8px rgba(34, 197, 94, 0.5)',
                                        } }), _jsx(Text, { style: {
                                            fontSize: '0.75rem',
                                            color: 'var(--text-muted)',
                                            fontWeight: 500,
                                        }, children: "System Online" })] }) })] }) }), _jsx(MantineAppShell.Main, { style: {
                    backgroundColor: 'var(--bg-base)',
                    minHeight: '100vh',
                    transition: 'background-color 200ms ease',
                }, children: _jsx(Box, { style: { padding: '24px 32px' }, children: children }) })] }));
}
//# sourceMappingURL=AppShell.js.map